import {Injectable} from '@angular/core';
import {HttpInterceptorService} from '../../core/services/http-interceptor.service';
import {DynamicSearchPayload, Filter, Sort, Target} from "@ispm-shared/modules/common/dynamic-search";
import {IspmHttpClient} from "@ispm/core/services/ispm-http-client";
import {catchError, map} from "rxjs/operators";
import {Observable, throwError} from "rxjs";
import {ActivityActions} from "@ispm/user/constants/activityActions";
import {set} from 'lodash';

@Injectable()
export class ActivityService {
  baseUrl = '/activity';
  dynamicSearchUrl = 'entity-tree/activity/dynamicSearch?type=clubbed';

  constructor(private _http: HttpInterceptorService, private _ispmHttpClient: IspmHttpClient) {
  }

  public create = (activity: any): any => {
    return this._http.post(this.baseUrl + '/', activity);
  }

  public getByContext = (context: any, actor: any) => {
    return this._http.get(this.baseUrl, {context: context, actor: actor}).toPromise()
      .then((data: any) => {
        if (data)
          return data.json();
      })
      .catch((err) => {
        console.error(`ActivityService :: GET => get activity error => context: ${JSON.stringify(context)} - actor: ${actor} - error: ${err}"`);
        throw (err);
      });
  }

  public get = (activityId: string) => {
    return this._http.get(this.baseUrl, {_id: activityId}).toPromise()
      .then((data: any) => {
        if (data)
          return data.json();
      })
      .catch((err) => {
        console.error(`ActivityService :: GET => get activity error => activityId: ${JSON.stringify(activityId)} - error: ${err}"`);
        throw (err);
      });
  }

  public put = (activity: any) => {
    return this._http.put(this.baseUrl, {activity: activity}).toPromise()
      .then((data: any) => {
        if (data)
          return data.json();
      })
      .catch((err) => {
        console.error(`ActivityService :: PUT => activity update error => activity: ${JSON.stringify(activity)} - error: ${err}"`);
        throw (err);
      });
  }

  public search = (dynamicSearchQuery: DynamicSearchPayload): Observable<any> => {
    return this._ispmHttpClient.post(this.dynamicSearchUrl, dynamicSearchQuery).pipe(
      catchError(err => {
        return throwError(err);
      }),
      map((response) => {
        return response.data;
      }));
  }

  public createTaskActivity = (actorId, taskId, taskType, additionalContext: Array<any> = []) => {
    let activity = {
      actor: {
        reference: actorId,
        resourceType: 'Practitioner'
      },
      context: [{
        reference: taskId,
        resourceType: taskType
      }],
      action: ActivityActions.CREATE,
      sampleAt: new Date().toISOString()
    };

    if (additionalContext)
      activity.context.push(...additionalContext);

    return this.create(activity);
  }

  public updateActivityAction = (activity: any, actorId: string, action: ActivityActions) => {
    set(activity, 'action', action);
    set(activity, 'sampleAt', new Date().toISOString());
    return this.put(activity);
  }

  public updateActivityContext = (activity: any, context: string) => {
    //For the case the activity's sample time was updated before the task ID (for example), the record's version will be changed as well.
    //So we will retrieve the activity's latest record from the DB in order to update its context.
    return this.get(activity.id)
      .then((data) => {
        if (data && data.length > 0) {
          let newActivity = data[0];
          set(newActivity.context[0], 'reference', context);
          set(newActivity, 'sampleAt', new Date().toISOString());
          return this.put(newActivity);
        }
      });
  }

  public updateActivitySampleTime = (activity: any) => {
    set(activity, 'sampleAt', new Date().toISOString());
    return this.put(activity);
  }

  public searchActivity = (context: string, actor: string, action: string, actionOperator: string = '=', sortByLatestFirst: boolean = true): Observable<any> => {
    return this.search(this.buildDynamicSearchQuery(context, actor, action, actionOperator, sortByLatestFirst));
  }

  private buildDynamicSearchQuery = (context: string, actor: string, action: string, actionOperator: string, sort: boolean): DynamicSearchPayload => {
    const dynamicSearchQuery: DynamicSearchPayload = new DynamicSearchPayload(new Target("ALL", 0));
    dynamicSearchQuery.filters = [];
    dynamicSearchQuery.relations = [];

    if (sort)
      dynamicSearchQuery.sort = [new Sort('Activity', '{meta,lastUpdated}', 'desc', 'dateTime')];

    if (actor)
      dynamicSearchQuery.filters.push(new Filter('Activity', '=', actor, '{actor}', 'reference'));

    if (context)
      dynamicSearchQuery.filters.push(new Filter('Activity', '=', context, '{context,resourceType}', 'string'));

    if (action)
      dynamicSearchQuery.filters.push(new Filter('Activity', actionOperator, action, '{action}', 'string'));

    return dynamicSearchQuery;
  }
}
